import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FadeIn, StaggerContainer, StaggerItem, RevealText } from '../components/AnimatedSection';
import { FloatingHexagons, HexShape } from '../components/HexagonLogo';
import { ParallaxImage, WordReveal, MagneticButton } from '../components/ScrollEffects';
import { HiArrowRight, HiCheck, HiStar } from 'react-icons/hi';

const plans = [
  {
    name: 'Starter', subtitle: 'Single Service', popular: false,
    features: ['One service of your choice', 'AI Video OR Content OR Research OR Web', 'Ideal for first engagement', '30-day delivery commitment', 'Dedicated account manager'],
  },
  {
    name: 'Growth', subtitle: '2–3 Services', popular: true,
    features: ['Any 2–3 services bundled', 'Integrated strategy across services', 'Monthly performance reporting', 'Priority support & revisions', 'Discounted bundle pricing'],
  },
  {
    name: 'Partner', subtitle: 'Full Retainer', popular: false,
    features: ['All 4 services fully integrated', 'Dedicated team of 4 specialists', 'Weekly strategy calls', 'Unlimited revisions', 'Preferred client status'],
  },
];

export default function Pricing() {
  return (
    <div className="page-wrapper">
      {/* Hero */}
      <section style={styles.heroSection}>
        <div style={styles.heroBg}>
          <motion.img
            src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&q=80"
            alt="Business planning"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
          />
          <div style={styles.heroOverlay} />
        </div>
        <FloatingHexagons />
        <div className="container" style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <FadeIn>
            <div className="section-num" style={{ justifyContent: 'center' }}>01 &mdash; INVESTMENT</div>
          </FadeIn>
          <RevealText>
            <h1 className="section-title" style={{ fontSize: 'clamp(2.8rem, 6vw, 4.2rem)' }}>
              Flexible engagements.<br /><span className="gradient-text">Serious results.</span>
            </h1>
          </RevealText>
          <FadeIn delay={0.3}>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              All engagements begin with a no-obligation discovery call.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Pricing Cards */}
      <section style={{ paddingBottom: 120 }}>
        <div className="container">
          <StaggerContainer className="grid-3" style={{ alignItems: 'stretch' }} staggerDelay={0.15}>
            {plans.map((plan, i) => (
              <StaggerItem key={i}>
                <motion.div
                  style={{ ...styles.card, ...(plan.popular ? styles.popularCard : {}) }}
                  whileHover={{ y: -8, borderColor: 'rgba(62, 117, 98, 0.3)' }}
                  transition={{ duration: 0.3 }}
                >
                  {plan.popular && (
                    <div style={styles.popularBadge}>
                      <HiStar size={12} /> Most Popular
                    </div>
                  )}
                  <HexShape size={36} color="#3E7562" opacity={plan.popular ? 0.25 : 0.12} />
                  <h3 style={styles.planName}>{plan.name}</h3>
                  <p style={styles.planSub}>{plan.subtitle}</p>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#3E7562', marginBottom: 8 }}>Custom Pricing</div>
                  <p style={{ fontSize: '0.75rem', color: '#777', marginBottom: 28 }}>Tailored to your goals</p>
                  <div style={styles.divider} />
                  <div style={styles.features}>
                    {plan.features.map((f, j) => (
                      <div key={j} style={styles.featItem}>
                        <HiCheck size={14} color="#3E7562" style={{ flexShrink: 0, marginTop: 2 }} />
                        <span style={styles.featText}>{f}</span>
                      </div>
                    ))}
                  </div>
                  <MagneticButton style={{ width: '100%' }}>
                    <Link to="/contact" className={plan.popular ? 'btn btn-primary' : 'btn btn-outline'} style={{ width: '100%', justifyContent: 'center', marginTop: 'auto' }}>
                      Book Discovery Call <HiArrowRight size={14} />
                    </Link>
                  </MagneticButton>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Parallax Break */}
      <ParallaxImage
        src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=80"
        alt="Team working together"
        height={350}
        speed={0.25}
      />

      {/* Bottom CTA */}
      <section className="section" style={{ background: '#111112' }}>
        <div className="container">
          <FadeIn>
            <div style={{ textAlign: 'center' }}>
              <WordReveal
                text="Not sure which plan fits?"
                className="section-title"
                style={{ fontSize: 'clamp(2rem, 4.5vw, 3rem)', fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.15, display: 'block', marginBottom: 16 }}
              />
              <p style={{ color: '#888', maxWidth: 450, margin: '0 auto 32px' }}>
                Start with a free discovery call. We'll recommend the right engagement.
              </p>
              <MagneticButton>
                <Link to="/contact" className="btn btn-primary" style={{ padding: '18px 48px' }}>
                  Schedule a Free Call <HiArrowRight />
                </Link>
              </MagneticButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

const styles = {
  heroSection: {
    minHeight: '60vh',
    display: 'flex',
    alignItems: 'center',
    paddingTop: 180,
    paddingBottom: 80,
    position: 'relative',
    overflow: 'hidden',
  },
  heroBg: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(11,11,11,0.9) 0%, rgba(11,11,11,0.8) 50%, rgba(11,11,11,0.95) 100%)',
  },
  card: { padding: 40, border: '1px solid rgba(255,255,255,0.04)', background: '#111112', display: 'flex', flexDirection: 'column', minHeight: 520, transition: '0.4s', position: 'relative' },
  popularCard: { border: '1px solid rgba(62,117,98,0.2)', background: 'linear-gradient(180deg, rgba(62,117,98,0.06) 0%, #111112 100%)' },
  popularBadge: { position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 18px', background: '#3E7562', color: '#fff', fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: 1 },
  planName: { fontSize: '1.4rem', fontWeight: 700, marginTop: 16, marginBottom: 4, letterSpacing: '-0.5px' },
  planSub: { color: '#777', fontSize: '0.85rem', marginBottom: 20 },
  divider: { height: 1, background: 'rgba(255,255,255,0.04)', marginBottom: 24 },
  features: { display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 32, flex: 1 },
  featItem: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  featText: { fontSize: '0.85rem', color: '#999', lineHeight: 1.4 },
};
